import { useCallback } from "react";
import { CONFIRMATION_TYPES, TRIGGER_TYPES } from "../../../common/constants";
import { validateMissionStops } from "../utils";

interface UseMissionValidationProps {
  missionName: string;
  missionStops: MissionStop[];
  isShowUnhitchWarning: boolean;
  availableStops?: Stop[];
}

export const useMissionValidation = ({
  missionName,
  missionStops,
  isShowUnhitchWarning,
  availableStops = [],
}: UseMissionValidationProps) => {
  const validateMission = useCallback(() => {
    const errors: string[] = [];

    // Validate mission name
    if (!missionName.trim()) {
      errors.push("Mission name is required");
    } else if (missionName.trim().length > 25) {
      errors.push("Mission name cannot exceed 25 characters");
    }

    // Validate that mission has at least one station
    if (missionStops.length === 0) {
      errors.push("Mission must have at least one station");
    }

    // Validate that all stations have a valid stopPk
    missionStops.forEach((stop, stopIndex) => {
      if (!stop.stopPk || stop.stopPk.trim() === "") {
        errors.push(
          `Station ${stopIndex + 1} must have a valid station selected`
        );
      } else {
        // Check if the selected station exists in the available stops
        const stationExists = availableStops.some(
          (availableStop) => availableStop.id === stop.stopPk
        );
        if (!stationExists) {
          errors.push(
            `Station ${stopIndex + 1} has an invalid station selected`
          );
        }
      }
    });

    // Validate double confirmation rules
    const doubleConfirmationErrors = validateMissionStops(missionStops);
    errors.push(...doubleConfirmationErrors);

    // Validate actions and trigger types (only for stops without double confirmation)
    missionStops.forEach((stop, stopIndex) => {
      // Skip validation for stops with double confirmation
      if (
        stop.confirmationType ===
        CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION
      ) {
        return;
      }

      stop.missionStopVdaActions.forEach(
        (action: VdaAction, actionIndex: number) => {
          // Validate action type
          if (!action.actionType || action.actionType.trim() === "") {
            errors.push(
              `Action type is required for action ${
                actionIndex + 1
              } in station ${stopIndex + 1}`
            );
          }

          // Validate trigger type
          if (
            !action.actionParameters?.[0]?.triggerType ||
            action.actionParameters[0].triggerType.trim() === ""
          ) {
            errors.push(
              `Trigger type is required for action ${
                actionIndex + 1
              } in station ${stopIndex + 1}`
            );
          }

          // Validate duration for DURATION trigger types
          if (
            action.actionParameters?.[0]?.triggerType === TRIGGER_TYPES.DURATION
          ) {
            const duration = action.actionParameters[0].duration;
            if (!duration) {
              errors.push(
                `Duration is required for action ${
                  actionIndex + 1
                } in station ${stopIndex + 1}`
              );
            }
          }
        }
      );
    });

    // Validate unhitch warning
    if (isShowUnhitchWarning) {
      errors.push("A mission can only have one unhitch event");
    }

    return errors;
  }, [missionName, missionStops, isShowUnhitchWarning, availableStops]);

  return {
    validateMission,
  };
};
